import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKeyboardShortcutsContext } from '../../contexts/KeyboardShortcutsContext';
import { logger } from '../../utils/logger';
import { usePreloadCollaborators } from '../../hooks/queries/useCollaborators';
import ProjectSwitchAnimation from '../shared/ProjectSwitchAnimation';
import { useTopicUIStore } from '../../store/topicUIStore';
import { useQueryClient } from '@tanstack/react-query';
import { topicKeys } from '../../hooks/queries/topicKeys';
import { useProjects, useUpdateLastSelectedAt } from '../../hooks/queries/useProjects';

/**
 * Component that handles keyboard shortcut for switching between dashboard and recent projects
 * This is a headless component that just initializes the Tab key shortcut
 */
export function ProjectSwitchHandler() {
  const { resetTopicUIState } = useTopicUIStore();
  const { registerShortcut } = useKeyboardShortcutsContext();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [switchingToProject, setSwitchingToProject] = useState<string | null>(null);

  // Get the preload collaborators function from React Query
  const { preload: preloadCollaborators } = usePreloadCollaborators();

  // Get projects from React Query
  // Using a stable default parameter to prevent unnecessary re-renders
  const showArchived = false;
  const { data: projects = [] } = useProjects(showArchived);

  // Get the updateLastSelectedAt mutation
  const updateLastSelectedAtMutation = useUpdateLastSelectedAt();

  // Track if we're currently on the dashboard
  const isDashboard = location.pathname === '/dashboard';

  // Extract the current active project ID from the URL
  const activeProjectId =
    !isDashboard && location.pathname.startsWith('/project/')
      ? location.pathname.split('/')[2]
      : null;

  // Keep track of the last non-dashboard project for switching back
  const lastProjectRef = useRef<string | null>(null);

  // Update lastProjectRef when navigating to a project
  useEffect(() => {
    if (activeProjectId && !isDashboard) {
      lastProjectRef.current = activeProjectId;
    }
  }, [activeProjectId, isDashboard]);

  /**
   * Get the most recently used project based on last_selected_at timestamp
   * Projects are already sorted by last_selected_at in descending order from the useProjects hook
   */
  const getRecentProjects = useCallback(() => {
    // Filter out the currently active project first
    const otherProjects = projects.filter(p => p.id !== activeProjectId);

    // First item is the second most recently used project
    return otherProjects.length > 0 ? otherProjects[0] : null;
  }, [projects, activeProjectId]);

  /**
   * Handle animation for project switching
   */
  const handleSwitchAnimation = useCallback((name: string) => {
    // Set animation state
    setSwitchingToProject(name);

    // Animation will be cleared by the ProjectSwitchAnimation component itself
    // which uses CSS transitions instead of timeouts
  }, []);

  /**
   * Switch between dashboard and recent project, or between projects
   * Enhanced to support dashboard toggle behavior
   */
  const switchProjectOrDashboard = useCallback(async () => {
    // If on dashboard, switch to the most recent project
    if (isDashboard) {
      // First try to use the cached last project
      const targetProjectId = lastProjectRef.current;

      // If we have a cached project, navigate to it
      if (targetProjectId) {
        logger.component('Switching from dashboard to last project', {
          to: targetProjectId,
        });

        // Use the cached project ID
        const targetProject = projects.find(p => p.id === targetProjectId);

        if (targetProject) {
          // Show animation
          handleSwitchAnimation(targetProject.name);

          // Reset topic UI state to clear any active topic
          resetTopicUIState();

          // Prefetch topics for the target project using byProject pattern for consistent caching
          queryClient.prefetchQuery({
            queryKey: topicKeys.byProject(targetProjectId),
            staleTime: 30 * 1000, // 30 seconds
          });

          // Preload collaborators using React Query
          preloadCollaborators(targetProjectId);

          // Update last selected timestamp
          await updateLastSelectedAtMutation.mutateAsync(targetProjectId);

          // Navigate to project
          navigate(`/project/${targetProjectId}`);
        }
      } else {
        // Fall back to the most recent project if no cached project
        const mostRecentProject = getRecentProjects();

        if (mostRecentProject) {
          logger.component('Switching from dashboard to most recent project', {
            to: mostRecentProject.id,
          });

          // Show animation
          handleSwitchAnimation(mostRecentProject.name);

          // Reset topic UI state to clear any active topic
          resetTopicUIState();

          // Prefetch topics for the target project using byProject pattern for consistent caching
          queryClient.prefetchQuery({
            queryKey: topicKeys.byProject(mostRecentProject.id),
            staleTime: 30 * 1000, // 30 seconds
          });

          // Preload collaborators using React Query
          preloadCollaborators(mostRecentProject.id);

          // Update last selected timestamp
          await updateLastSelectedAtMutation.mutateAsync(mostRecentProject.id);

          // Navigate to project
          navigate(`/project/${mostRecentProject.id}`);
        }
      }
    } else {
      // If we're already on a project page
      if (activeProjectId) {
        // Two options: either go to dashboard or switch to another project
        // First check if we have another recent project to switch to
        const recentProject = getRecentProjects();

        if (recentProject) {
          // We have another project to switch to, so let's do that instead of going to dashboard
          logger.component('Switching from current project to another project', {
            from: activeProjectId,
            to: recentProject.id,
          });

          // Cache the current project before switching away
          lastProjectRef.current = activeProjectId;

          // Show animation
          handleSwitchAnimation(recentProject.name);

          // Reset topic UI state to clear any active topic
          resetTopicUIState();

          // Prefetch topics for the target project using byProject pattern for consistent caching
          queryClient.prefetchQuery({
            queryKey: topicKeys.byProject(recentProject.id),
            staleTime: 30 * 1000, // 30 seconds
          });

          // Preload collaborators using React Query
          preloadCollaborators(recentProject.id);

          // Update last selected timestamp
          await updateLastSelectedAtMutation.mutateAsync(recentProject.id);

          // Navigate to project
          navigate(`/project/${recentProject.id}`);
        } else {
          // No other projects to switch to, so go to dashboard
          // Cache the current project ID before switching away
          lastProjectRef.current = activeProjectId;

          // Switch to dashboard
          logger.component('Switching from project to dashboard (no other recent projects)', {
            from: activeProjectId,
          });

          // Show animation
          handleSwitchAnimation('Dashboard');

          // Navigate to dashboard
          navigate('/dashboard');
        }
      } else {
        // Fallback behavior if no active project
        logger.component('No active project but not on dashboard, switching to dashboard');
        navigate('/dashboard');
      }
    }
  }, [
    isDashboard,
    projects,
    activeProjectId,
    getRecentProjects,
    navigate,
    handleSwitchAnimation,
    queryClient,
    resetTopicUIState,
    updateLastSelectedAtMutation,
  ]);

  // Use ref to always get the latest switchProjectOrDashboard function
  const switchProjectOrDashboardRef = useRef(switchProjectOrDashboard);
  switchProjectOrDashboardRef.current = switchProjectOrDashboard;

  // Register the keyboard shortcut (only once on mount)
  useEffect(() => {
    const shortcut = {
      id: 'switch_between_dashboard_and_projects',
      combo: 'tab',
      handler: (event: KeyboardEvent) => {
        // Skip if tab is pressed in an input element (preserve normal tab behavior)
        const activeElement = document.activeElement;
        const isInput =
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement ||
          activeElement?.isContentEditable ||
          activeElement?.tagName === 'BUTTON' ||
          activeElement?.hasAttribute('tabindex');

        if (isInput) return;

        // Handle the project/dashboard switching using the latest function
        switchProjectOrDashboardRef.current();

        // Prevent default tab behavior
        event.preventDefault();
      },
      description: 'Switch between dashboard and projects',
      scope: 'global',
      allowInInputs: false,
    };

    const unregister = registerShortcut(shortcut);

    // Log when the component mounts (only once now)
    logger.component('ProjectSwitchHandler: Component mounted');

    return unregister;
  }, [registerShortcut]); // Only depend on registerShortcut

  // Return the animation component with current project name
  return <ProjectSwitchAnimation projectName={switchingToProject} />;
}

export default ProjectSwitchHandler;
